<template>
  <div class="idcard-template-preview">
    <!-- Each face is the card's own HTML in a sandboxed iframe, so the only
         controls are the ones below — no annotate / draw / print / save
         toolbar from a browser PDF viewer. -->
    <div class="preview-toolbar">
      <div class="preview-toolbar-group">
        <b-button size="sm" variant="light" class="preview-tool-btn" :disabled="currentPage <= 1"
                  title="Previous page" @click="goToPage(currentPage - 1)">
          <b-icon icon="chevron-up"></b-icon>
        </b-button>
        <b-button size="sm" variant="light" class="preview-tool-btn" :disabled="currentPage >= faces.length"
                  title="Next page" @click="goToPage(currentPage + 1)">
          <b-icon icon="chevron-down"></b-icon>
        </b-button>
        <span class="preview-label">{{ currentPage }} of {{ faces.length }}</span>
      </div>

      <div class="preview-toolbar-group">
        <b-button size="sm" variant="light" class="preview-tool-btn" :disabled="zoomIndex <= 0"
                  title="Zoom out" @click="stepZoom(-1)">
          <b-icon icon="dash"></b-icon>
        </b-button>
        <span class="preview-label">{{ Math.round(zoom * 100) }}%</span>
        <b-button size="sm" variant="light" class="preview-tool-btn" :disabled="zoomIndex >= zoomSteps.length - 1"
                  title="Zoom in" @click="stepZoom(1)">
          <b-icon icon="plus"></b-icon>
        </b-button>
      </div>
    </div>

    <div ref="scroller" class="preview-scroller" :style="{ height: height + 'px' }" @scroll="onScroll">
      <div class="preview-pages">
        <div
          v-for="(face, index) in faces"
          :key="index"
          ref="pageEls"
          class="preview-page"
          :style="boxStyle(index)"
        >
          <iframe
            :srcdoc="faceHtml(face)"
            sandbox="allow-same-origin"
            scrolling="no"
            class="preview-frame"
            :style="frameStyle(index)"
            :title="'Card face ' + (index + 1)"
            @load="onFaceLoad(index, $event)"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* Fallback box size, used only for the instant between the iframe attaching
   and its content being measured; the face is hidden until then. */
const UNMEASURED_SIZE = { width: 240, height: 360 };

export default {
  name: 'TemplatePreview',
  props: {
    // One card face per entry, in face order: { html, width, height }, where
    // width/height are the printed card's box in CSS px. A face may also be a
    // bare HTML string, in which case it is drawn at whatever size it reflows
    // to. See IdCardTemplateController::cardBox().
    faces: { type: Array, default: () => [] },
    height: { type: Number, default: 600 }
  },
  data() {
    return {
      zoomSteps: [0.5, 0.75, 1, 1.25, 1.5, 2, 3],
      zoomIndex: 2,
      sizes: [],
      currentPage: 1
    };
  },
  computed: {
    zoom() {
      return this.zoomSteps[this.zoomIndex];
    }
  },
  watch: {
    faces() {
      this.sizes = [];
      this.currentPage = 1;
    }
  },
  methods: {
    faceHtml(face) {
      return typeof face === 'string' ? face : (face && face.html) || '';
    },
    // The printed card's own box, as declared by the template design. Every
    // face of a card reports the same one, which is what keeps the front and
    // the back the same size on screen.
    cardFor(index) {
      const face = this.faces[index];
      if (!face || typeof face === 'string') return null;
      return face.width > 0 && face.height > 0
        ? { width: face.width, height: face.height }
        : null;
    },
    // What the face actually reflowed to in the iframe.
    sizeFor(index) {
      return this.sizes[index] || UNMEASURED_SIZE;
    },
    // A browser lays this markup out a few percent taller than mPDF does, so a
    // face can overflow the card box it belongs in. Shrink it back to fit
    // rather than clipping it — the whole face stays visible, at the exact
    // card size. Never enlarge: a face that reflows short just leaves card
    // background below it, the same as the printed card does.
    fitFor(index) {
      const card = this.cardFor(index);
      if (!card) return 1;
      const size = this.sizeFor(index);
      if (!size.width || !size.height) return 1;
      return Math.min(1, card.width / size.width, card.height / size.height);
    },
    // The box carries the zoomed footprint so the scroller lays out correctly;
    // the iframe keeps its natural size and is scaled into it, which zooms the
    // card as drawn instead of reflowing its contents.
    boxStyle(index) {
      const box = this.cardFor(index) || this.sizeFor(index);
      return {
        width: Math.round(box.width * this.zoom) + 'px',
        height: Math.round(box.height * this.zoom) + 'px',
        visibility: this.sizes[index] ? 'visible' : 'hidden'
      };
    },
    frameStyle(index) {
      const size = this.sizeFor(index);
      const card = this.cardFor(index);
      const scale = this.zoom * this.fitFor(index);
      const boxWidth = (card ? card.width : size.width) * this.zoom;
      return {
        width: size.width + 'px',
        height: size.height + 'px',
        transform: 'scale(' + scale + ')',
        left: Math.max(0, (boxWidth - size.width * scale) / 2) + 'px'
      };
    },
    onFaceLoad(index, event) {
      this.measureFace(index, event.target);

      // The card font arrives after load, and swapping it in can change the
      // face's height, so measure again once it has settled.
      const doc = this.frameDocument(event.target);
      if (doc && doc.fonts && doc.fonts.ready && doc.fonts.ready.then) {
        doc.fonts.ready.then(() => this.measureFace(index, event.target)).catch(() => {});
      }
    },
    frameDocument(frame) {
      try {
        return frame.contentDocument || null;
      } catch (e) {
        return null;
      }
    },
    // Collapsing the frame first is what makes the measurement meaningful:
    // scroll size never reports less than the viewport, so the frame has to be
    // smaller than the card on both axes before it reveals the card's own
    // size. Height matters as much as width — leaving the frame at its
    // previous height floors every later measurement at that height, so a face
    // could only ever grow, never shrink back.
    measureFace(index, frame) {
      const doc = this.frameDocument(frame);
      if (!doc || !doc.documentElement) return;

      const restoreWidth = frame.style.width;
      const restoreHeight = frame.style.height;
      frame.style.width = '1px';
      frame.style.height = '1px';
      const width = Math.ceil(doc.documentElement.scrollWidth);
      frame.style.width = width + 'px';
      const height = Math.ceil(doc.documentElement.scrollHeight);
      frame.style.width = restoreWidth;
      frame.style.height = restoreHeight;

      if (!width || !height) return;

      const current = this.sizes[index];
      if (current && current.width === width && current.height === height) return;

      this.$set(this.sizes, index, { width, height });
    },
    stepZoom(direction) {
      const next = this.zoomIndex + direction;
      if (next < 0 || next >= this.zoomSteps.length) return;
      const page = this.currentPage;
      this.zoomIndex = next;
      this.$nextTick(() => this.goToPage(page));
    },
    goToPage(pageNumber) {
      const scroller = this.$refs.scroller;
      const elements = this.$refs.pageEls;
      if (!scroller || !elements || !elements[pageNumber - 1]) return;
      scroller.scrollTop = elements[pageNumber - 1].offsetTop - elements[0].offsetTop;
      this.currentPage = pageNumber;
    },
    onScroll() {
      const scroller = this.$refs.scroller;
      const elements = this.$refs.pageEls;
      if (!scroller || !elements || !elements.length) return;
      let visible = 1;
      elements.forEach((element, index) => {
        if (element.offsetTop - elements[0].offsetTop <= scroller.scrollTop + 40) {
          visible = index + 1;
        }
      });
      this.currentPage = visible;
    }
  }
};
</script>

<style scoped>
.idcard-template-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}
.preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}
.preview-toolbar-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.preview-tool-btn {
  border: 1px solid #dee2e6;
}
.preview-label {
  font-size: 13px;
  color: #495057;
  min-width: 62px;
  text-align: center;
}
.preview-scroller {
  overflow: auto;
  background: #525659;
}
.preview-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
}
.preview-page {
  position: relative;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  flex: none;
}
.preview-frame {
  position: absolute;
  top: 0;
  border: none;
  display: block;
  transform-origin: top left;
}
</style>
